import { useEffect, useState } from "react";
import { Alert, Card, Col, Container, Row, Tab, Tabs } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { getRegion, setOrgToken } from "../../controllers/org";
import { useAuth } from "../../components/auth/AuthProvider";
import ComunidadMembresíaGrafica from "../../components/comunidad/comunidadMembresiaGrafica";
import ComunidadMembresíaTabla from "../../components/comunidad/comunidadMembresiaTabla";
import Loading from "../../components/loading/Loading";
import CrearDiocesis from "../../components/admin/CrearDiocesis";
import ErrorAlert from "../../components/errors/ErrorAlert";
import Members from "./Members";
import ComunidadOrdenarLibros from "../../components/comunidad/comunidadOrdenarLibros";
import ComunidadOrderHistory from "../../components/comunidad/comunidadOrderHistory";
import YearSelector from "../../components/common/YearSelector";
import useYearParam from "../../hooks/useYearParam";
import ServiciosEnComunidades from './ServiciosEnComunidades';

function Region() {
  const params = useParams();
  const regionId = params.regionId;
  const [ciclo, onCicloChange] = useYearParam();
  const [data, setData] = useState();
  const [errorCode, setErrorCode] = useState(); 
  const { token } = useAuth();
  setOrgToken(token);

  useEffect(() => {
    loadData();
  }, [ciclo]);

  useEffect(() => {
    if (data) {
      document.title = `FAMA - Región - ${data.region.label}`;
    }
  }, [data]);

  async function loadData() {
    try {
      let r = await getRegion(regionId, ciclo);
      console.log('region', r);
      setData(r);
    }
    catch (e) {
      console.log(e);
      if (!isNaN(e.status)) {
        setErrorCode(e.status);
      }
    }
  }

  if (errorCode) {
    return <Container className='pt-5 pb-5'>
      <ErrorAlert code={errorCode} />
    </Container>;
  }
  if (!data) {
    return <Container className='pt-5 pb-5'>
      <Loading text="Cargando..." />
    </Container>;
  }

  function renderListOfDiocesis() {
    let body = 'No hay diócesis en esta región.'
    if (data.region.dioceses.length) {
      body = data.region.dioceses.map(c => {
        return <p><Link to={`/app/org/diocesis/${c.diocesis_id}?ciclo=${ciclo}`}>{c.label}</Link></p>
      });
    }
    return <Card>
          <Card.Header>Diócesis</Card.Header>
          <Card.Body>
            {body}
          </Card.Body>
        </Card>
  }
  let contenidoResumen;
  if (!data.members.members.count) {
    contenidoResumen = <Row>
      <Col md={6}>
        <Alert variant="warning">
          <p>No hay miembros en esta región.</p>
        </Alert>
      </Col>
      <Col md={6}>
        {renderListOfDiocesis()}
        <CrearDiocesis defaultRegion={data.region} onSuccess={loadData} />
      </Col>
    </Row>
  }
  else {
    contenidoResumen = <Row>
            <Col md={4}>
              <ComunidadMembresíaGrafica membresia={data.members.groups[0].diocesis} title="Membresía Por Diócesis" />
            </Col>
            <Col md={4}>
              <ComunidadMembresíaTabla 
                membresia={data.members.groups[0].diocesis} 
                total={{count: data.members.groups[0].members_count, 
                paid: data.members.groups[0].paid_members_count}} 
                title="Resumen de Membresía" 
                groupByLabel="Diócesis"
                link={true}
                />
            </Col>
            <Col md={4}>
              {renderListOfDiocesis()}
              <CrearDiocesis defaultRegion={data.region} onSuccess={loadData} />
            </Col>
          </Row>;
  }

  let nacionalPath = `/app/org/nacional?ciclo=${ciclo}`;
    let nacionalLink = data.verMembresiaNacional ?
      <Link to={nacionalPath}>Nacional</Link> :
      'Nacional';

  const comunidadesDesdeRegion = (data?.region?.dioceses ?? []).flatMap((diocesis) => {
    const comunidadesDeDiocesis = Array.isArray(diocesis?.comunidades)
      ? diocesis.comunidades
      : Object.values(diocesis?.comunidades || {});

    return comunidadesDeDiocesis.map((comunidad) => {
      const comunidadId = Number(comunidad.comunidad_id);
      const miembros = (data?.members?.members?.rows ?? []).filter((miembro) => Number(miembro.comunidad_id) === comunidadId);

      return {
        ...comunidad,
        comunidad_id: comunidadId,
        presidente: miembros.find((miembro) => miembro.membership_roles?.some((mr) => mr.role_id === 23)) || null,
        tesorero: miembros.find((miembro) => miembro.membership_roles?.some((mr) => mr.role_id === 26)) || null,
      };
    });
  });

  const comunidadesDesdeAgrupacion = (data?.members?.groups ?? []).flatMap((grupo) => {
    const diocesis = Array.isArray(grupo?.diocesis) ? grupo.diocesis : Object.values(grupo?.diocesis || {});
    return diocesis.flatMap((item) => {
      const comunidades = Array.isArray(item?.comunidades)
        ? item.comunidades
        : Object.values(item?.comunidades || {});

      return comunidades.map((comunidad) => {
        const comunidadId = Number(comunidad.comunidad_id);
        const miembros = (data?.members?.members?.rows ?? []).filter((miembro) => Number(miembro.comunidad_id) === comunidadId);

        return {
          ...comunidad,
          comunidad_id: comunidadId,
          presidente: miembros.find((miembro) => miembro.membership_roles?.some((mr) => mr.role_id === 23)) || null,
          tesorero: miembros.find((miembro) => miembro.membership_roles?.some((mr) => mr.role_id === 26)) || null,
        };
      });
    });
  });

  const comunidades = [...new Map(
    [...comunidadesDesdeRegion, ...comunidadesDesdeAgrupacion]
      .map((comunidad) => [Number(comunidad.comunidad_id), comunidad])
  ).values()].sort((a, b) => a.label.localeCompare(b.label));

  return <Container className='pt-5 pb-5'>
      <Row className="mb-3">
        <Col><h1>{data.region.label}</h1></Col>
        <Col md={3} className="ms-auto">
          <YearSelector
            value={ciclo}
            onChange={onCicloChange}
            keyPrefix="region-year"
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <p>{nacionalLink}</p>
        </Col>
      </Row>
      <Tabs>
        <Tab eventKey="resumen" title="Resumen">
          {contenidoResumen}
        </Tab>
        <Tab eventKey="miembros" title="Miembros">
          <Members showYearSelector={false} />
        </Tab>
        <Tab eventKey="libros" title="Ordenar Libros">
          <Row>
            <Col lg={8}><ComunidadOrdenarLibros 
              type="region"
              comunidadId={params.comunidadId}
              comunidades={comunidades}
              matrimonioPresidente={data.members.members.rows.filter(u => u.membership_roles.find(mr => mr.role_id === 20))[0]}
              matrimonioTesorero={data.members.members.rows.filter(u => u.membership_roles.find(mr => mr.role_id === 26))[0]}
            /></Col>
          </Row>
        </Tab>
        <Tab eventKey="order-history" title="Historial de órdenes" mountOnEnter={true} unmountOnExit={true}>
          <ComunidadOrderHistory type="region" />
        </Tab>
        <Tab eventKey="servicios-comunidades" title="Servicios en Comunidades" mountOnEnter={true} unmountOnExit={true}>
          <ServiciosEnComunidades scope="region" scopeId={data.region.region_id} ciclo={ciclo} />
        </Tab>
      </Tabs>
  </Container>;
}

export default Region;
